/******************************************************************************
 * Created Date: Thursday June 25th 2020                                      *
 * Author: Sean W.                                                            *
 * -----                                                                      *
 * Last Modified: Sun Aug 29 2021                                             *
 * Modified By: Sean W.                                                       *
 * -----                                                                      *
 * File: /src/components/common/DebugPanel.js                                 *
 * Copyright (c) 2020 MultiSensor                                             *
 ******************************************************************************/

import { useAuth0 } from "@auth0/auth0-react";
import { Clear as ClearIcon } from "@mui/icons-material";
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import React from "react";
import ReactJson from "react-json-view";
import { connect } from "react-redux";

import * as types from "../../actions/actionTypes";
import Loading from "../common/Loading";

const PREFIX = "DebugPanel";

const classes = {
  basic: `${PREFIX}-basic`,
  spinner: `${PREFIX}-spinner`,
  formControl: `${PREFIX}-formControl`,
  formControlFullWidth: `${PREFIX}-formControlFullWidth`,
  selectEmpty: `${PREFIX}-selectEmpty`,
  filtersCol: `${PREFIX}-filtersCol`,
  jsonCol: `${PREFIX}-jsonCol`,
  jsonHold: `${PREFIX}-jsonHold`
};

const Root = styled("div")(({ theme }) => ({
  [`.${classes.basic}`]: {
    width: "100%",
    height: "100vh",
    backgroundColor: "black",
    padding: 30,
    flex: "1",
    color: "white"
  },

  [`.${classes.spinner}`]: {
    width: "100%",
    height: "100%",
    textAlign: "center"
  },

  [`.${classes.formControl}`]: {
    margin: theme.spacing(1),
    minWidth: 120
  },

  [`.${classes.formControlFullWidth}`]: {
    margin: theme.spacing(1),
    minWidth: 120,
    width: "100%"
  },

  [`.${classes.selectEmpty}`]: {
    marginTop: theme.spacing(2)
  },

  [`.${classes.filtersCol}`]: {
    backgroundColor: "wheat"
  },

  [`.${classes.jsonCol}`]: {
    backgroundColor: "rgb(12, 13, 14)",
    overflow: "hidden",
    width: "100%",
    height: "100%"
  },

  [`.${classes.jsonHold}`]: {
    width: "100%",
    height: "100%",
    overflow: "scroll"
  }
}));

const DebugPanel = ({ iotMessages, deviceIds, messageTypes, clearHistory }) => {
  const { isLoading } = useAuth0();
  const [camera, setCamera] = React.useState("");
  const [messageType, setMessageType] = React.useState("");

  const handleCameraChange = event => {
    setCamera(event.target.value);
  };

  const handleTypeChange = event => {
    setMessageType(event.target.value);
  };

  if (isLoading) {
    return (
      <Root className={classes.spinner}>
        <Loading />
      </Root>
    );
  }

  // This filters all messages by the selected camera ID
  let filtered = iotMessages.filter(msg => msg.deviceId === camera || !camera);

  // This filters all messages by the message type
  filtered = filtered.filter(
    msg => msg.messageSource === messageType || !messageType
  );

  return (
    <div className={classes.basic}>
      <span>Total IoT Hub messages: {iotMessages.length}</span>
      <Grid container spacing={3} className={classes.jsonCol}>
        <Grid item xs={8} className={classes.jsonHold}>
          <ReactJson
            src={filtered}
            theme="brewer"
            displayObjectSize={false}
            displayDataTypes={false}
            collapsed={2}
          />
        </Grid>
        <Grid item xs={4} className={classes.filtersCol}>
          <Grid container spacing={3}>
            <Grid item lg={6} md={12}>
              <FormControl
                variant="outlined"
                className={classes.formControlFullWidth}
              >
                <InputLabel id="outlined-type-select">Camera</InputLabel>
                <Select
                  variant="standard"
                  labelId="outlined-camera-select-label"
                  value={camera}
                  onChange={handleCameraChange}
                  label="Message Type"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {deviceIds.map(msg => (
                    <MenuItem key={msg} value={msg}>
                      {msg}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item lg={6} md={12}>
              <FormControl
                variant="outlined"
                className={classes.formControlFullWidth}
              >
                <InputLabel id="outlined-type-select">Message Type</InputLabel>
                <Select
                  variant="standard"
                  labelId="outlined-type-select-label"
                  value={messageType}
                  onChange={handleTypeChange}
                  label="Message Type"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {messageTypes.map(msg => (
                    <MenuItem key={msg} value={msg}>
                      {msg}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl variant="outlined" className={classes.formControl}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={clearHistory}
                  className={classes.button}
                  startIcon={<ClearIcon />}
                >
                  Clear
                </Button>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

const mapStateToProps = (state /*, ownProps*/) => {
  return {
    iotMessages: state.iotMessages,
    deviceIds: [...new Set(state.iotMessages.map(msg => msg.deviceId))],
    messageTypes: [...new Set(state.iotMessages.map(msg => msg.messageSource))]
  };
};

const mapDispatchToProps = dispatch => {
  return {
    clearHistory: () => dispatch({ type: types.CLEAR_IOT_MESSAGES })
  };
};

export function insertNewIoTMessage(message) {
  return { type: types.NEW_IOT_MESSAGE, message };
}

DebugPanel.propTypes = {
  iotMessages: PropTypes.array.isRequired,
  deviceIds: PropTypes.array.isRequired,
  messageTypes: PropTypes.array.isRequired,
  clearHistory: PropTypes.func.isRequired
};

export default connect(mapStateToProps, mapDispatchToProps)(DebugPanel);
